// biome-ignore-all lint/a11y/noNoninteractiveTabindex: Row groups support keyboard reordering and focus restoration.
import { defineCatalog } from "@json-render/core"
import { defineRegistry } from "@json-render/react"
import { schema } from "@json-render/react/schema"
import { useQueryClient } from "@tanstack/react-query"
import { LosslessNumber } from "lossless-json"
import { ArrowUpRight, CircleSlash, Loader2 } from "lucide-react"
import {
  Children,
  type ComponentProps,
  type ComponentType,
  createContext,
  Fragment,
  type KeyboardEvent,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { z } from "zod"
import type { Unavailable } from "./bindings"
import { allows, type ConditionOutcome, type ConditionSources, evaluateCondition } from "./conditions"
import { encodeContextValue } from "./context"
import { asString, exactText, isRecord, stringifyExact } from "./json"
import { message, noticeOf, rendererNotice } from "./messages"
import { MetricBuckets } from "./metric-buckets"
import { objectTypeOf, text, type UxField, type UxNode, type UxPlacement, type WidgetArtifact } from "./model"
import { Badge, cn, Button as DefaultButton, ErrorState, Input, Skeleton, Value } from "./primitives"
import {
  type BindingState,
  type PageRuntime,
  presentationFor,
  RecordScope,
  type RowRecord,
  type RuntimeRecord,
  useConditionSources,
  useCurrentRecord,
  useRecordOf,
  useRuntime,
  type WidgetHost,
  WidgetHostError,
} from "./runtime"
import { buildActionInput, canonicalVersion, type SubmissionNotice } from "./submit"

const nodeProps = z.object({ nodeId: z.string() })

/** Host presentation receives admitted content only; conditions, reads and actions stay here. */
export interface WidgetListPresentationProps {
  readonly columns: readonly { readonly key: string; readonly header: ReactNode }[]
  readonly rows: readonly { readonly key: string; readonly cells: readonly ReactNode[] }[]
  readonly footer?: ReactNode
}

export interface WidgetPresentation {
  readonly Button?: ComponentType<ComponentProps<typeof DefaultButton>>
  readonly Section?: ComponentType<{ title?: ReactNode; children: ReactNode }>
  readonly List?: ComponentType<WidgetListPresentationProps>
  readonly Detail?: ComponentType<{ identity: ReactNode; actions?: ReactNode; children: ReactNode }>
  readonly Form?: ComponentType<{ children: ReactNode }>
  readonly Actions?: ComponentType<{ children: ReactNode; status?: ReactNode }>
  readonly formClassName?: string
  readonly fieldClassName?: string
}

const PresentationContext = createContext<WidgetPresentation>({})
export const WidgetPresentationProvider = PresentationContext.Provider

function Button(props: ComponentProps<typeof DefaultButton>) {
  const Control = useContext(PresentationContext).Button ?? DefaultButton
  return <Control {...props} />
}

function DefaultSection({ title, children }: { title?: ReactNode; children: ReactNode }) {
  return (
    <section className="grid gap-3">
      {title ? <h2 className="text-sm font-medium">{title}</h2> : null}
      {children}
    </section>
  )
}

function DefaultDetail({
  identity,
  actions,
  children,
}: {
  identity: ReactNode
  actions?: ReactNode
  children: ReactNode
}) {
  return (
    <div className="grid gap-1 divide-y divide-border-subtle rounded-lg border border-border-subtle px-4 py-2">
      <div className="flex items-center gap-2 pb-2">
        {identity}
        {actions}
      </div>
      {children}
    </div>
  )
}

function DefaultActions({ children, status }: { children: ReactNode; status?: ReactNode }) {
  return (
    <>
      <div className="flex flex-wrap items-center gap-2 pt-1">{children}</div>
      {status}
    </>
  )
}

/** The one catalog: every element the projection can emit, and nothing else. */
export const catalog = defineCatalog(schema, {
  components: {
    Section: { props: nodeProps, description: "A WidgetIR section: label, explicit fields, then children" },
    TabSet: {
      props: z.object({ tabs: z.array(z.object({ nodeId: z.string(), label: z.string() })) }),
      description: "Consecutive tab sections",
    },
    List: {
      props: z.object({ nodeId: z.string(), rowNodeIds: z.array(z.string()) }),
      description: "A bounded object list with explicit fields",
    },
    Detail: { props: nodeProps, description: "The binding's current record and its children" },
    Form: { props: nodeProps, description: "An action-input form with one declared submit" },
    ChildGrid: { props: nodeProps, description: "An editable, bounded ordered collection owned by one parent form" },
    Field: { props: nodeProps, description: "One field presentation" },
    Action: { props: nodeProps, description: "One action placement" },
    Navigation: { props: nodeProps, description: "One navigation item" },
    Unsupported: { props: z.object({ reason: z.string() }), description: "A component this shell does not render yet" },
  },
  actions: {},
})

/** A read or feature the shell cannot serve yet, with the issue that tracks it. */
export function UnavailableBlock({ why, compact = false }: { why: Unavailable; compact?: boolean }) {
  return (
    <div
      className={cn(
        "flex items-start gap-2 rounded-md border border-dashed border-border text-xs text-muted-foreground",
        compact ? "px-2 py-1" : "px-3 py-2.5",
      )}
    >
      <CircleSlash className="mt-0.5 size-3.5 shrink-0 text-subtle-foreground" aria-hidden />
      <span>
        {why.reason}
        {why.issue ? <span className="text-subtle-foreground"> (#{why.issue})</span> : null}
      </span>
    </div>
  )
}

/** A condition Norn could not evaluate here: shown with its reason, never as a default. */
function NotEvaluated({ expression, reason }: { expression: string; reason: string }) {
  const runtime = useRuntime()
  const evaluating = reason === message(runtime.locale, "condition.evaluatingTitle")
  return (
    <span
      title={`${expression}: ${evaluating ? message(runtime.locale, "condition.evaluatingTitle") : reason}`}
      className="inline-flex items-center gap-1 rounded-sm border border-dashed border-border px-1.5 py-0.5 text-2xs text-subtle-foreground"
    >
      {evaluating ? (
        <Loader2 className="size-3 animate-spin" aria-hidden />
      ) : (
        <CircleSlash className="size-3" aria-hidden />
      )}{" "}
      {evaluating ? message(runtime.locale, "condition.evaluating") : message(runtime.locale, "condition.unavailable")}
    </span>
  )
}

/** The reason an outcome withholds its control. */
function reasonOf(outcome: ConditionOutcome | null): string {
  return outcome?.kind === "unavailable" ? outcome.reason : ""
}

interface FormState {
  readonly formId: string
  readonly sessionKey: string
  readonly draft: Record<string, unknown>
  readonly setDraft: (name: string, value: unknown) => void
  readonly gridOperations: ReadonlyMap<
    string,
    { readonly inputName: string; readonly operations: readonly unknown[]; readonly ready: boolean }
  >
  readonly setGridOperations: (
    nodeId: string,
    inputName: string,
    operations: readonly unknown[] | null,
    ready: boolean,
  ) => void
  /** Inputs whose text is not a valid value of their type; submission waits until they are fixed. */
  readonly invalid: ReadonlySet<string>
  readonly busy: boolean
  readonly submitted: boolean
  readonly parentChanged: boolean
  readonly parentUnavailable: boolean
  readonly canReload: boolean
  readonly resetEpoch: number
  readonly setInvalid: (name: string, invalid: boolean) => void
  readonly reloadSession: () => boolean
}

interface ChildRowState {
  readonly formId: string
  readonly collectionId: string
  readonly gridId: string
  readonly orderingKey: string
  readonly position: number
  readonly bindingId: string | null
  readonly rowId: string
  readonly version: string
  readonly values: Record<string, unknown>
  readonly setValue: (propertyId: string, value: unknown) => void
  readonly missingFields: ReadonlySet<string>
}

const DECIMAL = /^-?(0|[1-9]\d*)(\.\d+)?([eE][-+]?\d+)?$/

/** Editable text for a draft value: exact digits for preserved numbers. */
function editText(value: unknown): string {
  return exactText(value) ?? (typeof value === "string" ? value : typeof value === "number" ? String(value) : "")
}

/** The record values a condition may read, as plain records. */
/** Sources for one list row: the row answers for its own binding, everything else as before. */
function withRow(
  sources: ConditionSources,
  bindingId: string,
  row: { values: unknown; key: string },
): ConditionSources {
  return {
    ...sources,
    record: (id) =>
      id === bindingId ? { values: row.values as Record<string, unknown>, key: row.key } : sources.record(id),
  }
}

/** The first outcome that withholds the control, if any. */
function withheld(outcomes: readonly (ConditionOutcome | null)[]): ConditionOutcome | null {
  return outcomes.find((o) => o !== null && o.kind === "unavailable") ?? null
}
const FormContext = createContext<FormState | null>(null)
const ChildRowContext = createContext<ChildRowState | null>(null)

/** Format hints select presentation only; the cell supplies every value and marking mask. */
function PropertyValue({ record, propertyId, field }: { record: RuntimeRecord; propertyId: string; field?: UxField }) {
  const runtime = useRuntime()
  const masked = record.masked !== undefined && Object.hasOwn(record.masked, propertyId)
  const value = masked ? undefined : (record.values as Record<string, unknown>)[propertyId]
  if (field?.format === "metric_buckets")
    return (
      <MetricBuckets value={value} label={text(field.label, runtime.locale)} locale={runtime.locale} masked={masked} />
    )
  if (masked) {
    // Render only the declared redaction marker, never a masked raw value.
    const marker =
      record.masked?.[propertyId]?.policy.kind === "redacted"
        ? "[REDACTED]"
        : message(runtime.locale, "field.restricted")
    return <span className="text-xs text-muted-foreground">{marker}</span>
  }
  return <Value value={value} />
}

function FieldView({ field }: { field: UxField }) {
  const runtime = useRuntime()
  const fieldClassName = useContext(PresentationContext).fieldClassName
  const form = useContext(FormContext)
  const row = useContext(ChildRowContext)
  const label = text(field.label, runtime.locale)
  const target = field.target
  const recordOf = useRecordOf()
  const record = target.kind === "property" ? recordOf(target.property.bindingId) : undefined
  const parentForm = target.kind === "parent_property" ? runtime.forms.get(target.formId) : undefined
  const parentRecord =
    target.kind === "parent_property" && parentForm?.dataBindingId ? recordOf(parentForm.dataBindingId) : undefined
  const rowMatches =
    target.kind === "child_row_property" && row?.formId === target.formId && row.collectionId === target.collectionId
  const rowSource =
    rowMatches && row?.bindingId
      ? { bindingId: row.bindingId, record: { key: row.rowId, values: row.values, version: row.version } }
      : undefined
  // Each condition reads exactly the sources it declares, by id; Norn evaluates it.
  const sources = useConditionSources(
    rowSource,
    form ? { formId: form.formId, values: form.draft } : undefined,
    rowMatches && row ? { gridId: row.gridId, rowId: row.rowId, values: row.values } : undefined,
  )
  const visible = evaluateCondition(field.visibleWhen, sources, runtime.evaluator, runtime.locale)
  const readOnly = evaluateCondition(field.readOnlyWhen, sources, runtime.evaluator, runtime.locale)
  const required = evaluateCondition(field.requiredWhen, sources, runtime.evaluator, runtime.locale)
  const pending = withheld([visible, readOnly, required])
  if (visible?.kind === "value" && !visible.value) return null
  if (target.kind === "form_input" || target.kind === "parent_property" || target.kind === "child_row_property") {
    if (!form || form.formId !== target.formId)
      return <UnavailableBlock compact why={{ reason: message(runtime.locale, "field.outsideForm", { label }) }} />
    if (target.kind === "parent_property" && !parentForm)
      return (
        <UnavailableBlock compact why={{ reason: message(runtime.locale, "field.parentUnavailable", { label }) }} />
      )
    if (target.kind === "child_row_property" && !rowMatches)
      return <UnavailableBlock compact why={{ reason: message(runtime.locale, "field.outsideRow", { label }) }} />
    const entity =
      target.kind === "child_row_property"
        ? childEntityForGrid(runtime, target.formId, target.collectionId, row?.bindingId ?? undefined)
        : target.kind === "parent_property"
          ? entityForForm(runtime, target.formId)
          : undefined
    const property = target.kind === "form_input" ? undefined : effectiveProperty(runtime, entity, target.propertyId)
    const control =
      target.kind === "parent_property" && generatedProperty(runtime, entity, target.propertyId)
        ? "display"
        : field.control
    const emptyValue = property?.nullable ? null : undefined
    const parentDraft =
      target.kind === "parent_property" && isRecord(form.draft[target.inputName])
        ? (form.draft[target.inputName] as Record<string, unknown>)
        : undefined
    const value =
      target.kind === "form_input"
        ? form.draft[target.inputName]
        : target.kind === "parent_property"
          ? parentDraft && Object.hasOwn(parentDraft, target.propertyId)
            ? parentDraft[target.propertyId]
            : (parentRecord?.values as Record<string, unknown> | undefined)?.[target.propertyId]
          : row?.values[target.propertyId]
    const invalidKey =
      target.kind === "form_input"
        ? target.inputName
        : target.kind === "parent_property"
          ? `${target.inputName}.${target.propertyId}`
          : `row:${row?.rowId}:${field.fieldId}`
    const updateValue = (next: unknown) => {
      if (control === "display") return
      if (target.kind === "form_input") form.setDraft(target.inputName, next)
      else if (target.kind === "parent_property") {
        const draftValue = form.draft[target.inputName]
        const current: Record<string, unknown> = isRecord(draftValue) ? draftValue : {}
        const nextValues = { ...current }
        // Parent updates are patch-like, but an empty parent property is an
        // explicit clear. Omitting the key would fall back to the old record
        // value in this field and leave the server value unchanged.
        if (next === undefined) nextValues[target.propertyId] = null
        else nextValues[target.propertyId] = next
        form.setDraft(target.inputName, nextValues)
      } else row?.setValue(target.propertyId, next)
    }
    const id = target.kind === "child_row_property" ? `field-${field.fieldId}-${row?.rowId}` : `field-${field.fieldId}`
    // A control under a condition Norn has not evaluated stays unavailable rather than guessing.
    const disabled =
      pending !== null ||
      (readOnly?.kind === "value" && readOnly.value) ||
      form.busy ||
      form.submitted ||
      (form.parentUnavailable && target.kind !== "form_input")
    const missing = rowMatches && row?.missingFields.has(field.fieldId) === true
    const invalid = missing || form.invalid.has(invalidKey)
    const errorId = `${id}-error`
    const requiredNow = property?.nullable === false || (required?.kind === "value" && required.value)
    const closedControl = control === "select" || control === "link"
    const choices = closedControl ? fieldChoices(runtime, field, row?.bindingId ?? undefined) : null
    const selectedChoice = choices?.findIndex((choice) => sameJsonValue(choice.value, value)) ?? -1
    const optionState = field.optionQueryBindingId ? runtime.bindings.get(field.optionQueryBindingId) : undefined
    const setText = (raw: string) => {
      if (control === "number") {
        const ok = raw === "" || DECIMAL.test(raw)
        form.setInvalid(invalidKey, !ok)
        // Numbers keep their exact digits end to end; nothing passes through a JavaScript number.
        updateValue(raw === "" ? emptyValue : ok ? new LosslessNumber(raw) : raw)
      } else updateValue(raw === "" && property?.nullable ? null : raw)
    }
    return (
      <div className="grid gap-1.5">
        <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
          <label htmlFor={id}>{label}</label>
          {requiredNow ? (
            <span aria-hidden className="text-destructive">
              *
            </span>
          ) : null}
        </div>
        {control === "display" ? (
          <div id={id} className="min-h-8 rounded-md border border-border-subtle bg-card px-2.5 py-1.5 text-sm">
            {value === undefined ? <span className="text-subtle-foreground">—</span> : <Value value={value} />}
          </div>
        ) : closedControl ? (
          <>
            <select
              id={id}
              disabled={disabled || choices === null}
              aria-required={requiredNow || undefined}
              aria-invalid={invalid || undefined}
              aria-describedby={invalid ? errorId : undefined}
              value={
                value === undefined || value === null ? "" : selectedChoice < 0 ? "current" : String(selectedChoice)
              }
              onChange={(event) => {
                if (event.target.value === "") updateValue(emptyValue)
                else {
                  const choice = choices?.find((_, index) => String(index) === event.target.value)
                  if (choice) updateValue(choice.value)
                }
              }}
              className={cn(
                "min-h-8 rounded-md border border-input bg-background px-2.5 py-1.5 text-sm outline-none focus-visible:border-primary/60 focus-visible:ring-3 focus-visible:ring-ring/40 disabled:opacity-50",
                fieldClassName,
              )}
            >
              <option value="">{message(runtime.locale, "field.notSet")}</option>
              {value !== undefined && value !== null && selectedChoice < 0 ? (
                <option value="current" disabled>
                  {editText(value)}
                </option>
              ) : null}
              {choices?.map((choice, index) => (
                <option key={index} value={String(index)}>
                  {choice.label}
                </option>
              ))}
            </select>
            {choices === null ? (
              <span role="status" className="text-2xs text-muted-foreground">
                {message(runtime.locale, "field.optionsUnavailable")}
              </span>
            ) : null}
            {field.optionQueryBindingId &&
            optionState?.status === "list" &&
            optionState.cursor !== null &&
            optionState.continues ? (
              <Button
                type="button"
                size="sm"
                variant="secondary"
                disabled={disabled || optionState.loadingMore}
                onClick={() => runtime.loadMore(field.optionQueryBindingId as string)}
              >
                {message(runtime.locale, "list.loadMore")}
              </Button>
            ) : null}
          </>
        ) : control === "text_area" ? (
          <textarea
            id={id}
            disabled={disabled}
            aria-invalid={invalid || undefined}
            aria-describedby={invalid ? errorId : undefined}
            aria-required={requiredNow || undefined}
            value={editText(value)}
            onChange={(e) => setText(e.target.value)}
            rows={3}
            className={cn(
              "rounded-md border border-input bg-background px-2.5 py-1.5 text-sm outline-none focus-visible:border-primary/60 focus-visible:ring-3 focus-visible:ring-ring/40 disabled:opacity-50",
              fieldClassName,
              "h-auto",
            )}
          />
        ) : control === "checkbox" ? (
          <div className="flex min-h-8 items-center gap-2">
            <input
              ref={(input) => {
                if (input) input.indeterminate = value === undefined || value === null
              }}
              id={id}
              type="checkbox"
              disabled={disabled}
              aria-invalid={invalid || undefined}
              aria-describedby={invalid ? errorId : undefined}
              aria-required={requiredNow || undefined}
              aria-checked={value === undefined || value === null ? "mixed" : value === true}
              checked={value === true}
              onChange={(e) => updateValue(e.target.checked)}
              className="size-4 accent-[var(--primary)]"
            />
            {value === undefined || value === null ? (
              <span className="text-xs text-muted-foreground">{message(runtime.locale, "field.notSet")}</span>
            ) : null}
          </div>
        ) : (
          <Input
            id={id}
            className={fieldClassName}
            disabled={disabled}
            aria-invalid={invalid || undefined}
            aria-describedby={invalid ? errorId : undefined}
            aria-required={requiredNow || undefined}
            inputMode={control === "number" ? "decimal" : undefined}
            type={control === "date" ? "date" : control === "date_time" ? "datetime-local" : "text"}
            value={editText(value)}
            onChange={(e) => setText(e.target.value)}
          />
        )}
        {invalid ? (
          <span id={errorId} className="text-2xs text-destructive">
            {message(runtime.locale, missing ? "field.requiredValue" : "field.invalidNumber")}
          </span>
        ) : null}
        {pending ? <NotEvaluated expression={conditionExpression(field, pending)} reason={reasonOf(pending)} /> : null}
      </div>
    )
  }
  // Property presentations display read-only values; edits belong to form inputs.
  return (
    <div
      className={
        field.format === "metric_buckets"
          ? "grid min-w-0 gap-4 py-3"
          : "grid min-w-0 items-baseline gap-1.5 py-2 sm:grid-cols-[minmax(100px,180px)_minmax(0,1fr)] sm:gap-4"
      }
    >
      <span
        className={
          field.format === "metric_buckets" ? "text-sm font-medium tracking-tight" : "text-xs text-muted-foreground"
        }
      >
        {label}
      </span>
      <div className="min-w-0 text-sm">
        {visible?.kind === "unavailable" ? (
          <NotEvaluated expression={field.visibleWhen?.expression ?? ""} reason={visible.reason} />
        ) : record ? (
          <PropertyValue record={record} propertyId={target.property.propertyId} field={field} />
        ) : (
          <span className="text-subtle-foreground">{message(runtime.locale, "field.noRecord")}</span>
        )}
      </div>
    </div>
  )
}

function conditionExpression(field: UxField, outcome: ConditionOutcome): string {
  const all = [field.visibleWhen, field.readOnlyWhen, field.requiredWhen].filter(Boolean)
  return outcome.kind === "unavailable" ? all.map((c) => c?.expression).join(" · ") || outcome.reason : ""
}

interface GridDraftRow {
  readonly rowId: string
  /** The original CAS value stays fixed through edits and reorder operations. */
  readonly expectedVersion: string
  readonly originalValues: Record<string, unknown>
  readonly values: Record<string, unknown>
  readonly isNew: boolean
}

interface GridDraft {
  readonly snapshotKey: string
  readonly rows: readonly GridDraftRow[]
  readonly removed: readonly {
    readonly row: GridDraftRow
    readonly index: number
    readonly invalidFieldIds: readonly string[]
  }[]
  readonly structureChanged: boolean
  readonly stale: boolean
}

interface GridFocusRequest {
  readonly rowId: string
  readonly target: "group" | "up" | "down" | "field"
  readonly fallbackToAdd?: boolean
}

type GridSnapshot =
  | { readonly kind: "ready"; readonly key: string; readonly rows: readonly GridDraftRow[] }
  | { readonly kind: "loading" }
  | {
      readonly kind: "incomplete"
      readonly message: string
      readonly canContinue: boolean
      readonly loadingMore: boolean
    }
  | { readonly kind: "unavailable"; readonly message: string }

function childGridSnapshot(
  state: BindingState | undefined,
  maxRows: number,
  orderingKey: string,
  create: boolean,
  host: WidgetHost,
  locale: string,
): GridSnapshot {
  if (create) return { kind: "ready", key: "create", rows: [] }
  if (!state || state.status === "loading") return { kind: "loading" }
  if (state.status === "error") return { kind: "unavailable", message: messageOf(state.error, host, locale) }
  if (state.status === "unavailable") return { kind: "unavailable", message: state.why.reason }
  if (state.status === "awaiting_selection")
    return { kind: "unavailable", message: message(locale, "grid.waitingParent", { bindingId: state.sourceBindingId }) }
  if (state.status !== "list") return { kind: "unavailable", message: message(locale, "grid.notList") }
  if (state.duplicateKeys?.size) return { kind: "unavailable", message: message(locale, "grid.duplicateId") }
  if (state.moreError)
    return {
      kind: "incomplete",
      message: message(locale, "grid.readFailed", { error: messageOf(state.moreError, host, locale) }),
      canContinue: state.cursor !== null && state.continues,
      loadingMore: state.loadingMore,
    }
  if (state.cursor !== null) {
    return {
      kind: "incomplete",
      message: state.loadingMore
        ? message(locale, "grid.loadingComplete")
        : !state.continues
          ? message(locale, "grid.noContinuation")
          : state.stalled
            ? message(locale, "grid.scanning")
            : message(locale, "grid.moreRows"),
      // Bounded empty-page following can pause with an advancing cursor. Let the operator continue;
      // the grid remains incomplete and non-editable until the cursor is exhausted.
      canContinue: state.continues,
      loadingMore: state.loadingMore,
    }
  }
  if (state.loadingMore || state.stalled)
    return {
      kind: "incomplete",
      message: message(locale, "grid.incomplete"),
      canContinue: false,
      loadingMore: state.loadingMore,
    }
  if (state.rows.length > maxRows)
    return { kind: "unavailable", message: message(locale, "grid.exceedsBound", { maxRows }) }
  const seen = new Set<string>()
  const ordered: { readonly row: GridDraftRow; readonly order: bigint }[] = []
  for (const record of state.rows) {
    const rowId = record.key
    const expectedVersion = canonicalVersion(record.version)
    const orderText = isRecord(record.values) ? canonicalVersion(record.values[orderingKey]) : undefined
    if (
      !rowId.trim() ||
      seen.has(rowId) ||
      expectedVersion === undefined ||
      orderText === undefined ||
      !isRecord(record.values)
    ) {
      return { kind: "unavailable", message: message(locale, "grid.invalidRow") }
    }
    seen.add(rowId)
    const values = { ...record.values }
    ordered.push({
      row: { rowId, expectedVersion, originalValues: values, values, isNew: false },
      order: BigInt(orderText),
    })
  }
  ordered.sort((left, right) => (left.order < right.order ? -1 : left.order > right.order ? 1 : 0))
  const rows = ordered.map(({ row, order }, index) => {
    if (order !== BigInt(index + 1)) return null
    return row
  })
  if (rows.some((row) => row === null)) return { kind: "unavailable", message: message(locale, "grid.invalidOrder") }
  const completeRows = rows as GridDraftRow[]
  const key = stringifyExact(completeRows.map((row) => [row.rowId, row.expectedVersion, row.values]))
  return { kind: "ready", key, rows: completeRows }
}

function sameJsonValue(left: unknown, right: unknown): boolean {
  if (left === undefined || right === undefined) return left === right
  return stringifyExact(left) === stringifyExact(right)
}

function rowChanged(row: GridDraftRow, fields: readonly UxField[]): boolean {
  return (
    row.isNew ||
    fields.some((field) => {
      if (field.target.kind !== "child_row_property") return false
      const property = field.target.propertyId
      return !sameJsonValue(row.originalValues[property], row.values[property])
    })
  )
}

function operationsForGrid(draft: GridDraft, collectionId: string, fields: readonly UxField[]): readonly unknown[] {
  const operations: unknown[] = []
  for (const row of draft.rows) {
    if (row.isNew) {
      const values: Record<string, unknown> = {}
      for (const field of fields) {
        if (field.target.kind !== "child_row_property" || field.control === "display") continue
        const value = row.values[field.target.propertyId]
        if (value !== undefined) values[field.target.propertyId] = value
      }
      operations.push({ collection: collectionId, op: "add", rowId: row.rowId, expectedVersion: "0", values })
      continue
    }
    const values: Record<string, unknown> = {}
    for (const field of fields) {
      if (field.target.kind !== "child_row_property" || field.control === "display") continue
      const property = field.target.propertyId
      if (!sameJsonValue(row.originalValues[property], row.values[property])) {
        // Null is an explicit clear; omission would silently preserve the old value.
        values[property] = row.values[property] === undefined ? null : row.values[property]
      }
    }
    if (Object.keys(values).length > 0) {
      operations.push({
        collection: collectionId,
        op: "update",
        rowId: row.rowId,
        expectedVersion: row.expectedVersion,
        values,
      })
    }
  }
  for (const { row } of draft.removed) {
    if (!row.isNew)
      operations.push({
        collection: collectionId,
        op: "remove",
        rowId: row.rowId,
        expectedVersion: row.expectedVersion,
      })
  }
  if (draft.structureChanged) {
    operations.push({
      collection: collectionId,
      op: "reorder",
      rowIds: draft.rows.map((row) => ({ rowId: row.rowId, expectedVersion: row.isNew ? "0" : row.expectedVersion })),
    })
  }
  return operations
}

function gridIsDirty(draft: GridDraft, fields: readonly UxField[]): boolean {
  return draft.structureChanged || draft.removed.length > 0 || draft.rows.some((row) => rowChanged(row, fields))
}

function ChildGridView({
  nodeId,
  formId,
  collectionId,
  label,
  orderingKey,
  dataBindingId,
  rowOpsInputName,
  maxRows,
  fieldIds,
}: {
  readonly nodeId: string
  readonly formId: string
  readonly collectionId: string
  readonly label: string
  readonly orderingKey: string
  readonly dataBindingId?: string
  readonly rowOpsInputName: string
  readonly maxRows: number
  readonly fieldIds: readonly string[]
}) {
  const runtime = useRuntime()
  const form = useContext(FormContext)
  const fields = useMemo(
    () => fieldIds.flatMap((id) => (runtime.fields.get(id) ? [runtime.fields.get(id) as UxField] : [])),
    [fieldIds, runtime.fields],
  )
  const writableFields = useMemo(() => {
    const derived = derivedChildProperties(runtime, formId, collectionId)
    return fields.filter(
      (field) =>
        field.target.kind === "child_row_property" &&
        field.control !== "display" &&
        !derived.has(field.target.propertyId),
    )
  }, [fields, runtime.actions, runtime.forms, runtime.set, formId, collectionId])
  const bindingState = dataBindingId ? runtime.bindings.get(dataBindingId) : undefined
  const formDefinition = runtime.forms.get(formId)
  const createWithoutSnapshot = !dataBindingId && formDefinition?.mode === "create"
  const snapshot = useMemo(
    () =>
      dataBindingId
        ? childGridSnapshot(bindingState, maxRows, orderingKey, false, runtime.host, runtime.locale)
        : createWithoutSnapshot
          ? childGridSnapshot(undefined, maxRows, orderingKey, true, runtime.host, runtime.locale)
          : ({ kind: "unavailable", message: message(runtime.locale, "grid.noBinding") } as const),
    [bindingState, dataBindingId, maxRows, orderingKey, createWithoutSnapshot, runtime.host, runtime.locale],
  )
  const [draft, setDraft] = useState<GridDraft | null>(null)
  const resetEpochRef = useRef(form?.resetEpoch ?? 0)
  const gridElement = useRef<HTMLElement | null>(null)
  const addButton = useRef<HTMLButtonElement | null>(null)
  const continueButton = useRef<HTMLButtonElement | null>(null)
  const rowGroups = useRef(new Map<string, HTMLDivElement>())
  const focusRequest = useRef<GridFocusRequest | null>(null)
  const focusAfterContinuation = useRef(false)
  const continuationWasLoading = useRef(false)
  useEffect(() => {
    if (snapshot.kind !== "ready") return
    if (form && resetEpochRef.current !== form.resetEpoch) {
      resetEpochRef.current = form.resetEpoch
      setDraft({
        snapshotKey: snapshot.key,
        rows: snapshot.rows.map((row) => ({
          ...row,
          originalValues: { ...row.originalValues },
          values: { ...row.values },
        })),
        removed: [],
        structureChanged: false,
        stale: false,
      })
      return
    }
    setDraft((current) => {
      if (!current)
        return { snapshotKey: snapshot.key, rows: snapshot.rows, removed: [], structureChanged: false, stale: false }
      if (form?.submitted) {
        if (current.snapshotKey === snapshot.key && !current.stale) return current
        return { snapshotKey: snapshot.key, rows: snapshot.rows, removed: [], structureChanged: false, stale: false }
      }
      if (current.snapshotKey === snapshot.key) return current
      if (gridIsDirty(current, writableFields)) return { ...current, stale: true }
      return { snapshotKey: snapshot.key, rows: snapshot.rows, removed: [], structureChanged: false, stale: false }
    })
  }, [snapshot, writableFields, form?.submitted, form?.resetEpoch])
  useLayoutEffect(() => {
    const request = focusRequest.current
    if (!request) return
    const group = rowGroups.current.get(request.rowId)
    let target: HTMLElement | null = null
    if (group) {
      if (request.target === "field") {
        for (const field of fields) {
          if (field.control === "display") continue
          const candidate = document.getElementById(`field-${field.fieldId}-${request.rowId}`)
          const isControl =
            candidate instanceof HTMLInputElement ||
            candidate instanceof HTMLTextAreaElement ||
            candidate instanceof HTMLSelectElement
          if (isControl && !candidate.disabled) {
            target = candidate
            break
          }
        }
      } else if (request.target === "up" || request.target === "down") {
        const button = group.querySelector<HTMLButtonElement>(`[data-grid-control="${request.target}"]`)
        if (button && !button.disabled) target = button
      }
      target ??= group
    }
    if (!target && request.fallbackToAdd) {
      target = addButton.current && !addButton.current.disabled ? addButton.current : gridElement.current
    }
    target?.focus({ preventScroll: true })
    focusRequest.current = null
  }, [draft?.rows, fields])
  const snapshotCurrent =
    snapshot.kind === "ready" &&
    draft?.snapshotKey === snapshot.key &&
    (!form || resetEpochRef.current === form.resetEpoch)
  useLayoutEffect(() => {
    if (snapshot.kind === "incomplete" && snapshot.loadingMore) {
      continuationWasLoading.current = true
      return
    }
    if (!continuationWasLoading.current || !focusAfterContinuation.current) return
    if (snapshot.kind === "ready" && !snapshotCurrent) return

    continuationWasLoading.current = false
    const active = document.activeElement
    const canRestoreFocus =
      active === document.body || active === continueButton.current || (gridElement.current?.contains(active) ?? false)
    if (canRestoreFocus) {
      if (snapshot.kind === "ready") gridElement.current?.focus({ preventScroll: true })
      else if (snapshot.kind === "incomplete") continueButton.current?.focus({ preventScroll: true })
    }
    focusAfterContinuation.current = false
  }, [snapshot, snapshotCurrent])
  const operations = useMemo(
    () => (draft && !draft.stale && snapshotCurrent ? operationsForGrid(draft, collectionId, writableFields) : null),
    [draft, snapshotCurrent, collectionId, writableFields],
  )
  const conditionSources = useConditionSources(undefined, form ? { formId, values: form.draft } : undefined)
  const childEntity = childEntityForGrid(runtime, formId, collectionId, dataBindingId)
  let requirednessUnavailable = false
  const missingByRow = new Map(
    (draft?.rows ?? []).map((row, index) => {
      const values = { ...row.values, [orderingKey]: index + 1 }
      const sources = { ...conditionSources, row: { gridId: nodeId, rowId: row.rowId, values } }
      const rowSources = dataBindingId ? withRow(sources, dataBindingId, { key: row.rowId, values }) : sources
      const missing = new Set(
        writableFields.flatMap((field) => {
          if (field.target.kind !== "child_row_property") return []
          const property = effectiveProperty(runtime, childEntity, field.target.propertyId)
          const required = evaluateCondition(field.requiredWhen, rowSources, runtime.evaluator, runtime.locale)
          if (required?.kind === "unavailable") {
            requirednessUnavailable = true
            return []
          }
          const value = row.values[field.target.propertyId]
          // Existing rows send patches: only changed values need local presence validation.
          if (!row.isNew && sameJsonValue(row.originalValues[field.target.propertyId], value)) return []
          // Omitted defaults are materialized by the kernel; explicit clears are not defaults.
          if (row.isNew && value === undefined && property?.defaultValue !== undefined) return []
          const requiredNow = property?.nullable === false || (required?.kind === "value" && required.value)
          return requiredNow && (value === undefined || value === null || value === "") ? [field.fieldId] : []
        }),
      )
      return [row.rowId, missing] as const
    }),
  )
  const complete = !requirednessUnavailable && [...missingByRow.values()].every((missing) => missing.size === 0)
  const ready = Boolean(
    form && form.formId === formId && snapshotCurrent && draft && !draft.stale && !form.parentUnavailable && complete,
  )
  const requestFocus = (request: GridFocusRequest) => {
    focusRequest.current = request
  }
  useEffect(() => {
    if (form?.formId === formId) form.setGridOperations(nodeId, rowOpsInputName, operations, ready)
  }, [form?.formId, form?.setGridOperations, form?.resetEpoch, formId, nodeId, rowOpsInputName, operations, ready])
  if (!form || form.formId !== formId)
    return <UnavailableBlock compact why={{ reason: message(runtime.locale, "grid.outsideForm") }} />
  if (snapshot.kind === "loading") return <Skeleton className="h-20 w-full" />
  if (snapshot.kind === "unavailable") return <UnavailableBlock why={{ reason: snapshot.message }} />
  if (snapshot.kind === "incomplete") {
    return (
      <div
        role="status"
        data-testid={`child-grid-${nodeId}`}
        className="grid gap-2 rounded-md border border-dashed border-border px-3 py-3 text-sm text-muted-foreground"
      >
        <span>
          {label}: {snapshot.message}
        </span>
        {snapshot.canContinue && dataBindingId ? (
          <Button
            ref={continueButton}
            size="sm"
            className="w-fit"
            aria-label={message(runtime.locale, "grid.loadCompleteLabel", { label })}
            disabled={snapshot.loadingMore}
            onClick={() => {
              focusAfterContinuation.current = true
              runtime.loadMore(dataBindingId)
            }}
          >
            {snapshot.loadingMore ? <Loader2 className="size-4 animate-spin" aria-hidden /> : null}
            {message(runtime.locale, "grid.loadComplete")}
          </Button>
        ) : null}
      </div>
    )
  }
  if (!draft) return <Skeleton className="h-20 w-full" />
  if (!draft.stale && !snapshotCurrent) return <Skeleton className="h-20 w-full" />
  const discardAndReload = () => {
    if (snapshot.kind !== "ready" || !form.canReload || form.busy || form.submitted) return
    requestFocus(
      snapshot.rows[0]
        ? { rowId: snapshot.rows[0].rowId, target: "group", fallbackToAdd: true }
        : { rowId: "", target: "group", fallbackToAdd: true },
    )
    if (!form.reloadSession()) focusRequest.current = null
  }
  if (draft.stale) {
    return (
      <div
        role="status"
        data-testid={`child-grid-${nodeId}`}
        className="grid gap-2 rounded-md border border-dashed border-border px-3 py-3 text-sm text-muted-foreground"
      >
        {form.submitted ? (
          <span>{message(runtime.locale, "grid.loadingSaved")}</span>
        ) : (
          <span>{message(runtime.locale, "grid.changed")}</span>
        )}
        {snapshot.kind === "ready" && !form.submitted && !form.parentChanged ? (
          <Button size="sm" className="w-fit" disabled={!form.canReload || form.busy} onClick={discardAndReload}>
            {message(runtime.locale, "form.reloadRows")}
          </Button>
        ) : null}
      </div>
    )
  }

  const canEdit = snapshotCurrent && !form.busy && !form.submitted && !form.parentUnavailable
  const reorder = (rowId: string, offset: -1 | 1, target: GridFocusRequest["target"]) => {
    const from = draft.rows.findIndex((row) => row.rowId === rowId)
    const to = from + offset
    if (from < 0 || to < 0 || to >= draft.rows.length || !canEdit) return
    requestFocus({ rowId, target })
    setDraft((current) => {
      if (!current || current.stale) return current
      const from = current.rows.findIndex((row) => row.rowId === rowId)
      const to = from + offset
      if (from < 0 || to < 0 || to >= current.rows.length) return current
      const rows = [...current.rows]
      const [row] = rows.splice(from, 1)
      rows.splice(to, 0, row as GridDraftRow)
      return { ...current, rows, structureChanged: true }
    })
  }
  const remove = (rowId: string) => {
    if (!canEdit) return
    const removedIndex = draft.rows.findIndex((candidate) => candidate.rowId === rowId)
    const removedRow = draft.rows[removedIndex]
    if (!removedRow) return
    const nextFocusRow = draft.rows[removedIndex + 1] ?? draft.rows[removedIndex - 1]
    requestFocus(
      nextFocusRow
        ? { rowId: nextFocusRow.rowId, target: "group" }
        : { rowId: "", target: "group", fallbackToAdd: true },
    )
    const invalidFieldIds = fields.flatMap((field) =>
      field.target.kind === "child_row_property" && form.invalid.has(`row:${removedRow.rowId}:${field.fieldId}`)
        ? [field.fieldId]
        : [],
    )
    for (const field of fields)
      if (field.target.kind === "child_row_property") form.setInvalid(`row:${removedRow.rowId}:${field.fieldId}`, false)
    setDraft((current) => {
      if (!current || current.stale) return current
      const index = current.rows.findIndex((candidate) => candidate.rowId === rowId)
      const row = current.rows[index]
      if (!row) return current
      return {
        ...current,
        rows: current.rows.filter((candidate) => candidate.rowId !== rowId),
        removed: row.isNew ? current.removed : [...current.removed, { row, index, invalidFieldIds }],
        structureChanged: true,
      }
    })
  }
  const undoRemoval = () => {
    if (!canEdit) return
    const last = draft.removed[draft.removed.length - 1]
    if (!last) return
    requestFocus({ rowId: last.row.rowId, target: "group" })
    for (const fieldId of last.invalidFieldIds) form.setInvalid(`row:${last.row.rowId}:${fieldId}`, true)
    setDraft((current) => {
      if (!current || current.stale || current.removed.length === 0) return current
      const removed = current.removed[current.removed.length - 1]
      if (!removed) return current
      const rows = [...current.rows]
      rows.splice(Math.min(removed.index, rows.length), 0, removed.row)
      return { ...current, rows, removed: current.removed.slice(0, -1), structureChanged: true }
    })
  }
  const moveByKeyboard = (event: KeyboardEvent<HTMLDivElement>, rowId: string) => {
    if (!event.altKey || (event.key !== "ArrowUp" && event.key !== "ArrowDown")) return
    if (
      event.target !== event.currentTarget &&
      event.target instanceof HTMLElement &&
      event.target.closest("input,textarea,select,button")
    )
      return
    event.preventDefault()
    reorder(rowId, event.key === "ArrowUp" ? -1 : 1, "group")
  }
  const canCreateId = typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
  const add = () => {
    if (!canCreateId || draft.rows.length >= maxRows || !canEdit) return
    const rowId = crypto.randomUUID()
    requestFocus({ rowId, target: "field" })
    const values = initialChildCheckboxes(runtime, formId, collectionId, dataBindingId, writableFields)
    setDraft((current) =>
      current && !current.stale
        ? {
            ...current,
            rows: [...current.rows, { rowId, expectedVersion: "0", originalValues: {}, values, isNew: true }],
            structureChanged: true,
          }
        : current,
    )
  }

  return (
    <section
      ref={gridElement}
      tabIndex={-1}
      aria-label={label}
      data-child-grid
      data-testid={`child-grid-${nodeId}`}
      className="grid gap-2 rounded-lg border border-border-subtle p-3"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm font-medium">
          {label}
          <span className="ms-2 text-xs font-normal text-muted-foreground">
            {message(runtime.locale, "grid.count", { count: draft.rows.length, maxRows })}
          </span>
        </div>
        <Button
          ref={addButton}
          data-grid-control="add"
          size="sm"
          type="button"
          disabled={!canEdit || draft.rows.length >= maxRows || !canCreateId}
          onClick={add}
        >
          {message(runtime.locale, "grid.add")}
        </Button>
      </div>
      <p className="text-2xs text-muted-foreground">{message(runtime.locale, "grid.keyboard")}</p>
      {requirednessUnavailable ? (
        <p role="status" className="text-xs text-destructive">
          {message(runtime.locale, "grid.requirednessUnavailable")}
        </p>
      ) : null}
      {draft.removed.length > 0 ? (
        <p role="status" className="text-xs text-muted-foreground">
          {message(runtime.locale, "grid.removed", { count: draft.removed.length })}{" "}
          <Button size="sm" type="button" variant="link" disabled={!canEdit} onClick={undoRemoval}>
            {message(runtime.locale, "grid.undoRemoval")}
          </Button>
        </p>
      ) : null}
      {!canCreateId ? (
        <span className="text-2xs text-destructive">{message(runtime.locale, "grid.noSecureId")}</span>
      ) : null}
      {draft.rows.length === 0 ? (
        <p className="text-xs text-muted-foreground">{message(runtime.locale, "grid.empty", { label })}</p>
      ) : null}
      <div className="grid gap-2">
        {draft.rows.map((row, index) => {
          const rowValues = { ...row.values, [orderingKey]: index + 1 }
          const setValue = (propertyId: string, value: unknown) =>
            setDraft((current) =>
              current && !current.stale
                ? {
                    ...current,
                    rows: current.rows.map((candidate) =>
                      candidate.rowId === row.rowId
                        ? {
                            ...candidate,
                            values:
                              value === undefined
                                ? omitKey(candidate.values, propertyId)
                                : { ...candidate.values, [propertyId]: value },
                          }
                        : candidate,
                    ),
                  }
                : current,
            )
          return (
            <div
              ref={(element) => {
                if (element) rowGroups.current.set(row.rowId, element)
                else rowGroups.current.delete(row.rowId)
              }}
              key={row.rowId}
              role="group"
              aria-label={message(runtime.locale, "grid.row", { position: index + 1 })}
              aria-keyshortcuts="Alt+ArrowUp Alt+ArrowDown"
              tabIndex={0}
              onKeyDown={(event) => moveByKeyboard(event, row.rowId)}
              className="grid gap-2 rounded-md border border-border-subtle bg-background p-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-xs font-medium text-muted-foreground">
                  {message(runtime.locale, "grid.position", { position: index + 1 })}
                </span>
                <div className="flex flex-wrap gap-1">
                  <Button
                    data-grid-control="up"
                    size="sm"
                    type="button"
                    aria-label={message(runtime.locale, "grid.moveUpLabel", { position: index + 1 })}
                    disabled={!canEdit || index === 0}
                    onClick={() => reorder(row.rowId, -1, "up")}
                  >
                    {message(runtime.locale, "grid.moveUp")}
                  </Button>
                  <Button
                    data-grid-control="down"
                    size="sm"
                    type="button"
                    aria-label={message(runtime.locale, "grid.moveDownLabel", { position: index + 1 })}
                    disabled={!canEdit || index === draft.rows.length - 1}
                    onClick={() => reorder(row.rowId, 1, "down")}
                  >
                    {message(runtime.locale, "grid.moveDown")}
                  </Button>
                  <Button
                    data-grid-control="remove"
                    size="sm"
                    type="button"
                    variant="destructive"
                    aria-label={message(runtime.locale, "grid.removeLabel", { position: index + 1 })}
                    disabled={!canEdit}
                    onClick={() => remove(row.rowId)}
                  >
                    {message(runtime.locale, "grid.remove")}
                  </Button>
                </div>
              </div>
              <ChildRowContext.Provider
                value={{
                  formId,
                  collectionId,
                  gridId: nodeId,
                  orderingKey,
                  position: index + 1,
                  bindingId: dataBindingId ?? null,
                  rowId: row.rowId,
                  version: row.expectedVersion,
                  values: rowValues,
                  setValue,
                  missingFields: missingByRow.get(row.rowId) ?? new Set(),
                }}
              >
                {fields.map((field) => (
                  <FieldView key={field.fieldId} field={field} />
                ))}
              </ChildRowContext.Provider>
            </div>
          )
        })}
      </div>
    </section>
  )
}

function omitKey(value: Record<string, unknown>, key: string): Record<string, unknown> {
  const next = { ...value }
  delete next[key]
  return next
}

type SubmissionResult =
  | { ok: true }
  | { ok: false; notice: SubmissionNotice }
  | { ok: false; error: unknown; principal?: boolean }

function submissionMessage(
  result: SubmissionResult,
  locale: string,
  host: WidgetHost,
  success: "action.accepted" | "form.submitted",
): string {
  if (result.ok) return message(locale, success)
  if ("notice" in result) return rendererNotice(result.notice, locale)
  const error = messageOf(result.error, host, locale)
  return result.principal ? message(locale, "action.principalFailed", { error }) : error
}

function useSubmit() {
  const runtime = useRuntime()
  const queryClient = useQueryClient()
  return async (
    bindingId: string,
    inputs: { draft: Record<string, unknown>; record: Parameters<typeof buildActionInput>[2]["record"] },
    onAccepted?: () => void,
  ): Promise<SubmissionResult> => {
    const binding = runtime.actions.get(bindingId)
    if (!binding) return { ok: false, notice: { key: "action.undeclared", values: { bindingId } } }
    const built = buildActionInput(binding, runtime.set, { ...inputs, context: runtime.context })
    if ("unavailable" in built) return { ok: false, notice: built.unavailable }
    if (runtime.principal.status === "loading") return { ok: false, notice: { key: "action.principalLoading" } }
    if (runtime.principal.status === "failed") {
      return { ok: false, error: runtime.principal.error, principal: true }
    }
    try {
      await runtime.host.action({
        bindingId,
        actionId: built.actionId,
        input: built.input,
        actor: runtime.principal.actor,
      })
    } catch (error) {
      if (error instanceof WidgetHostError && error.refusal?.category === "conflict") {
        await queryClient
          .invalidateQueries({
            queryKey: ["widget-binding", runtime.host.cacheKey, runtime.scope.orgId, runtime.scope.environmentId],
          })
          .catch(() => undefined)
      }
      return { ok: false, error }
    }
    // Keep inline edit sessions read-only until authoritative parent and child snapshots have been
    // refetched. A successful mutation must never reopen against the pre-submit CAS versions.
    onAccepted?.()
    await queryClient
      .invalidateQueries(
        { queryKey: ["widget-binding", runtime.host.cacheKey, runtime.scope.orgId, runtime.scope.environmentId] },
        { throwOnError: true },
      )
      .catch(() => undefined)
    return { ok: true }
  }
}

function messageOf(error: unknown, host: WidgetHost, locale: string): string {
  if (error instanceof WidgetHostError && error.refusal)
    return `${error.refusal.message}: ${error.refusal.explanation} (${error.refusal.code} · ${error.refusal.category})`
  return host.describeError?.(error) ?? rendererNotice(noticeOf(error), locale)
}

function Confirmable({
  label,
  confirmation,
  disabled,
  onConfirm,
  variant = "primary",
}: {
  label: string
  confirmation: UxPlacement["confirmation"]
  disabled?: boolean
  onConfirm: () => void
  variant?: "primary" | "secondary" | "destructive"
}) {
  const { locale } = useRuntime()
  const [asking, setAsking] = useState(false)
  const [typed, setTyped] = useState("")
  const phrase = text(confirmation.phrase, locale)
  if (confirmation.strength === "none" || !asking) {
    return (
      <Button
        size="sm"
        variant={
          confirmation.severity === "destructive" || confirmation.severity === "irreversible" ? "destructive" : variant
        }
        disabled={disabled}
        onClick={() => (confirmation.strength === "none" ? onConfirm() : setAsking(true))}
      >
        {label}
      </Button>
    )
  }
  return (
    <div
      role="group"
      aria-label={message(locale, "action.confirm", { label })}
      className="flex flex-wrap items-center gap-2 rounded-md border border-border bg-card px-3 py-2 text-xs"
    >
      <span className="text-muted-foreground">
        {text(confirmation.message, locale) || message(locale, "action.confirmFallback", { label })}
      </span>
      {confirmation.strength === "typed" ? (
        <Input
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          placeholder={phrase}
          aria-label={message(locale, "action.typeConfirm", { phrase })}
          className="h-7 w-48 text-xs"
        />
      ) : null}
      <Button
        size="sm"
        variant="ghost"
        onClick={() => {
          setAsking(false)
          setTyped("")
        }}
      >
        {message(locale, "common.cancel")}
      </Button>
      <Button
        size="sm"
        variant={confirmation.severity === "confirm" || confirmation.severity === "safe" ? "primary" : "destructive"}
        disabled={disabled || (confirmation.strength === "typed" && typed !== phrase)}
        onClick={() => {
          setAsking(false)
          setTyped("")
          onConfirm()
        }}
      >
        {label}
      </Button>
    </div>
  )
}

function ActionView({ placement, row }: { placement: UxPlacement; row?: RowRecord }) {
  const runtime = useRuntime()
  const submit = useSubmit()
  const form = useContext(FormContext)
  const binding = runtime.actions.get(placement.bindingId)
  // Each binding names its sources; resolve them from this placement's surroundings or say why not.
  const recordOf = useRecordOf(row)
  const recordBindingId = binding?.inputs.recordBindingId ?? null
  const record = recordBindingId ? (recordOf(recordBindingId) ?? null) : null
  const draft = binding?.inputs.draftFormId && form?.formId === binding.inputs.draftFormId ? form.draft : undefined
  const sources = useConditionSources(row, form ? { formId: form.formId, values: form.draft } : undefined)
  const [result, setResult] = useState<SubmissionResult | null>(null)
  const label = text(placement.label, runtime.locale)
  const enabled = evaluateCondition(placement.enabledWhen, sources, runtime.evaluator, runtime.locale)
  const missing: string | null = !binding
    ? message(runtime.locale, "action.undeclared", { bindingId: placement.bindingId })
    : placement.formId
      ? null
      : binding.inputs.selectionBindingId
        ? message(runtime.locale, "action.selectionUnavailable")
        : binding.inputs.recordBindingId && !record
          ? message(runtime.locale, "action.recordUnavailable", { bindingId: binding.inputs.recordBindingId })
          : binding.inputs.draftFormId && !draft
            ? message(runtime.locale, "action.draftUnavailable")
            : null
  if (missing) {
    return (
      <span title={missing}>
        <Button size="sm" disabled>
          {label}
        </Button>
      </span>
    )
  }
  if (!allows(enabled)) {
    return (
      <span
        title={
          enabled?.kind === "unavailable"
            ? message(runtime.locale, "action.enabledWhen", {
                expression: placement.enabledWhen?.expression,
                reason: enabled.reason,
              })
            : message(runtime.locale, "action.notEnabled", { expression: placement.enabledWhen?.expression })
        }
      >
        <Button size="sm" disabled>
          {label}
        </Button>
      </span>
    )
  }
  if (placement.formId) {
    const formId = placement.formId
    return (
      <Button
        size="sm"
        onClick={() =>
          runtime.openForm({
            formId,
            placementId: placement.placementId,
            record,
            recordBindingId: record ? recordBindingId : null,
          })
        }
      >
        {label}
      </Button>
    )
  }
  return (
    <span className="inline-flex flex-col items-start gap-1">
      <Confirmable
        label={label}
        confirmation={placement.confirmation}
        variant="secondary"
        disabled={(form?.invalid.size ?? 0) > 0}
        onConfirm={() =>
          void submit(placement.bindingId, {
            draft: draft ?? {},
            record: record && recordBindingId ? { bindingId: recordBindingId, record } : null,
          }).then(setResult)
        }
      />
      {result ? (
        <span className="text-2xs text-muted-foreground">
          {submissionMessage(result, runtime.locale, runtime.host, "action.accepted")}
        </span>
      ) : null}
    </span>
  )
}

export function NavigationView({
  itemId,
  row,
  application = false,
}: {
  itemId: string
  row?: RowRecord
  application?: boolean
}) {
  const runtime = useRuntime()
  const recordOf = useRecordOf(row)
  const sources = useConditionSources(row)
  const item = runtime.navigation.get(itemId)
  if (!item)
    return <UnavailableBlock compact why={{ reason: message(runtime.locale, "navigation.undeclared", { itemId }) }} />
  const dest = item.destination
  if (dest.kind !== "page")
    return (
      <UnavailableBlock
        compact
        why={{
          reason: message(runtime.locale, "navigation.unsupportedDestination", {
            label: text(item.label, runtime.locale),
          }),
        }}
      />
    )
  const visible = evaluateCondition(item.visibleWhen, sources, runtime.evaluator, runtime.locale)
  if (visible?.kind === "value" && !visible.value) return null
  if (visible?.kind === "unavailable") {
    return (
      <span
        title={message(runtime.locale, "navigation.shownWhen", {
          expression: item.visibleWhen?.expression,
          reason: visible.reason,
        })}
      >
        <Button size="sm" variant="ghost" disabled>
          {text(item.label, runtime.locale)}
        </Button>
      </span>
    )
  }
  const params: Record<string, string> = {}
  for (const [name, v] of Object.entries(dest.parameters)) {
    const raw =
      v.kind === "literal"
        ? v.value
        : v.kind === "context_parameter"
          ? runtime.context[v.parameterId]
          : v.property.propertyId === "$key"
            ? // `$key` is reserved metadata: the record's runtime key, never a business property.
              recordOf(v.property.bindingId)?.key
            : (recordOf(v.property.bindingId)?.values as Record<string, unknown> | undefined)?.[v.property.propertyId]
    const value = encodeContextValue(raw)
    if (value === null) {
      return (
        <Button
          size="sm"
          variant="ghost"
          disabled
          title={message(runtime.locale, "navigation.needsParameter", { name })}
        >
          {text(item.label, runtime.locale)}
        </Button>
      )
    }
    params[name] = value
  }
  const navigate = runtime.navigate
  if (!navigate)
    return (
      <UnavailableBlock
        compact
        why={{ reason: message(runtime.locale, "navigation.unavailable", { label: text(item.label, runtime.locale) }) }}
      />
    )
  return (
    <Button
      size="sm"
      variant={application && dest.pageId === runtime.page.pageId ? "secondary" : "ghost"}
      aria-current={application && dest.pageId === runtime.page.pageId ? "page" : undefined}
      title={message(runtime.locale, "navigation.requires", { capability: item.requiredCapability })}
      onClick={() => navigate(dest.pageId, params)}
    >
      {text(item.label, runtime.locale)} {application ? null : <ArrowUpRight className="rtl-mirror" />}
    </Button>
  )
}

export const { registry } = defineRegistry(catalog, {
  components: {
    Section: ({ props, children }) => {
      const runtime = useRuntime()
      const SectionFrame = useContext(PresentationContext).Section ?? DefaultSection
      const node = runtime.nodes.get(props.nodeId)
      const section = node?.component.kind === "section" ? node.component.section : null
      const sectionGrid =
        node &&
        node.children.length > 1 &&
        node.children.every((child) => child.component.kind === "section" && child.component.section.kind !== "tab")
      return (
        <SectionFrame title={section ? text(section.label, runtime.locale) : undefined}>
          {section?.fields.map((ref) => {
            const field = presentationFor(runtime.fields, ref.bindingId, ref.propertyId)
            return field ? <FieldView key={field.fieldId} field={field} /> : null
          })}
          {Children.count(children) > 0 ? (
            <div className={cn("grid min-w-0 items-start gap-6", sectionGrid && "lg:grid-cols-2 lg:gap-8")}>
              {children}
            </div>
          ) : null}
        </SectionFrame>
      )
    },
    TabSet: ({ props, children }) => {
      const [active, setActive] = useState(0)
      const panes = Children.toArray(children)
      return (
        <div>
          <div role="tablist" className="mb-3 flex gap-4 border-b border-border-subtle">
            {props.tabs.map((t, i) => (
              <button
                key={t.nodeId}
                role="tab"
                type="button"
                aria-selected={active === i}
                onClick={() => setActive(i)}
                className={cn(
                  "-mb-px border-b-2 pb-2 text-sm",
                  active === i
                    ? "border-primary text-foreground"
                    : "border-transparent text-muted-foreground hover:text-foreground",
                )}
              >
                {t.label}
              </button>
            ))}
          </div>
          {panes[active]}
        </div>
      )
    },
    List: ({ props }) => {
      const runtime = useRuntime()
      const ListFrame = useContext(PresentationContext).List
      const listSources = useConditionSources()
      const node = runtime.nodes.get(props.nodeId)
      if (node?.component.kind !== "list") return null
      const component = node.component
      const state = runtime.bindings.get(component.dataBindingId)
      const columns = component.fields.map((ref) => ({
        ref,
        field: presentationFor(runtime.fields, ref.bindingId, ref.propertyId),
      }))
      const rowNodes = props.rowNodeIds.flatMap((id) => (runtime.nodes.get(id) ? [runtime.nodes.get(id)] : []))
      const selecting = component.selection !== "none"
      const chosen = runtime.selections.get(component.dataBindingId) ?? []
      const query = runtime.ux.dataBindings.find((binding) => binding.bindingId === component.dataBindingId)?.query
      const rowAffordances = rowNodes.length > 0 || runtime.explain !== undefined
      const span = columns.length + (rowAffordances ? 1 : 0) + (selecting ? 1 : 0) + (runtime.host.openGraph ? 1 : 0)
      const head = (
        <thead>
          <tr className="border-b border-border-subtle bg-card text-start text-xs text-muted-foreground">
            {selecting ? <th className="w-8 px-3 py-1.5" aria-label={message(runtime.locale, "list.select")} /> : null}
            {columns.map(({ ref, field }) => (
              <th key={ref.propertyId} scope="col" className="px-3 py-1.5 font-normal">
                {field ? text(field.label, runtime.locale) : ref.propertyId}
              </th>
            ))}
            {rowAffordances ? (
              <th className="px-3 py-1.5" aria-label={message(runtime.locale, "list.rowActions")} />
            ) : null}
            {runtime.host.openGraph ? (
              <th className="px-3 py-1.5">
                <span className="sr-only">{message(runtime.locale, "detail.graph")}</span>
              </th>
            ) : null}
          </tr>
        </thead>
      )
      // Every state renders inside the list's own frame, so it is clear which list and columns are affected.
      const frame = (body: ReactNode) => (
        <div className="min-w-0 overflow-x-auto rounded-lg border border-border-subtle">
          <table className="w-full text-sm">
            <caption className="caption-bottom border-t border-border-subtle px-3 py-2 text-start text-2xs text-subtle-foreground">
              <span className="font-mono">{component.dataBindingId}</span>
              {state && (state.status === "list" || state.status === "detail") ? (
                <span> · {state.objectType}</span>
              ) : null}
              {query?.kind === "link_traversal" ? (
                <span>
                  {message(runtime.locale, "list.traversal", { linkId: "\u0000", bindingId: "\u0001" })
                    // biome-ignore lint/suspicious/noControlCharactersInRegex: These sentinel delimiters split translated labels around embedded identifiers.
                    .split(/(\u0000|\u0001)/)
                    .map((part, index) =>
                      part === "\u0000" ? (
                        <code key={index} className="font-mono">
                          {query.linkId}
                        </code>
                      ) : part === "\u0001" ? (
                        <code key={index} className="font-mono">
                          {query.sourceBindingId}
                        </code>
                      ) : (
                        part
                      ),
                    )}
                </span>
              ) : null}
            </caption>
            {head}
            {body}
          </table>
        </div>
      )
      const tableMessage = (content: ReactNode) =>
        frame(
          <tbody>
            <tr>
              <td colSpan={span} className="px-3 py-3">
                {content}
              </td>
            </tr>
          </tbody>,
        )
      if (!state || state.status === "loading") return tableMessage(<Skeleton className="h-16 w-full" />)
      if (state.status === "unavailable") return tableMessage(<UnavailableBlock why={state.why} />)
      if (state.status === "error")
        return tableMessage(<ErrorState error={state.error} what={component.dataBindingId} inline />)
      if (state.status === "awaiting_selection")
        return tableMessage(
          <span className="text-sm text-muted-foreground">
            {message(runtime.locale, "list.awaitingSelection", { bindingId: state.sourceBindingId })}
          </span>,
        )
      if (state.status !== "list") return null
      // A cursor means more may exist, whatever this page held; a stalled one is still being scanned.
      const continuation =
        state.cursor !== null ? (
          <span className="flex flex-wrap items-center gap-2 text-2xs text-subtle-foreground">
            {message(runtime.locale, state.stalled ? "list.stillScanning" : "list.moreMayExist")}
            {state.continues ? (
              <Button
                size="sm"
                variant="ghost"
                disabled={state.loadingMore}
                onClick={() => runtime.loadMore(component.dataBindingId)}
              >
                {state.loadingMore ? <Loader2 className="animate-spin" /> : null}
                {message(runtime.locale, state.stalled || state.rows.length === 0 ? "list.continue" : "list.loadMore")}
              </Button>
            ) : (
              <span>{message(runtime.locale, "list.noContinuation")}</span>
            )}
            {state.moreError ? (
              <span role="alert" className="text-destructive">
                {message(runtime.locale, "list.nextPageFailed", {
                  error: messageOf(state.moreError, runtime.host, runtime.locale),
                })}
              </span>
            ) : null}
          </span>
        ) : null
      if (state.rows.length === 0) {
        return tableMessage(
          continuation ?? (
            <span className="text-sm text-muted-foreground">{message(runtime.locale, "list.noRecords")}</span>
          ),
        )
      }
      const presentedColumns = [
        ...(selecting
          ? [{ key: "selection", header: <span className="sr-only">{message(runtime.locale, "list.select")}</span> }]
          : []),
        ...columns.map(({ ref, field }) => ({
          key: `field:${ref.propertyId}`,
          header: field ? text(field.label, runtime.locale) : ref.propertyId,
        })),
        ...(rowAffordances
          ? [{ key: "actions", header: <span className="sr-only">{message(runtime.locale, "list.rowActions")}</span> }]
          : []),
        ...(runtime.host.openGraph
          ? [{ key: "graph", header: <span className="sr-only">{message(runtime.locale, "detail.graph")}</span> }]
          : []),
      ]
      const presentedRows = state.rows.map((row) => ({
        key: row.key,
        cells: [
          ...(selecting
            ? [
                <input
                  key="selection"
                  type={component.selection === "single" ? "radio" : "checkbox"}
                  name={`select:${component.dataBindingId}`}
                  aria-label={message(runtime.locale, "list.selectRow", { key: row.key })}
                  className="size-3.5 accent-[var(--primary)]"
                  checked={chosen.includes(row.key)}
                  onChange={(event) =>
                    runtime.select(
                      component.dataBindingId,
                      component.selection === "single"
                        ? [row.key]
                        : event.target.checked
                          ? [...chosen, row.key]
                          : chosen.filter((key) => key !== row.key),
                    )
                  }
                />,
              ]
            : []),
          ...columns.map(({ ref, field }) => {
            const visible = evaluateCondition(
              field?.visibleWhen,
              withRow(listSources, component.dataBindingId, row),
              runtime.evaluator,
              runtime.locale,
            )
            return visible?.kind === "unavailable" ? (
              <NotEvaluated
                key={ref.propertyId}
                expression={field?.visibleWhen?.expression ?? ""}
                reason={visible.reason}
              />
            ) : allows(visible) ? (
              <div
                key={ref.propertyId}
                className={field?.format === "metric_buckets" ? "min-w-[240px] py-2" : undefined}
              >
                <PropertyValue record={row} propertyId={ref.propertyId} field={field} />
              </div>
            ) : null
          }),
          ...(rowAffordances
            ? [
                <RecordScope key="actions" bindingId={component.dataBindingId} record={row}>
                  <div className="flex flex-wrap justify-end gap-1.5">
                    {rowNodes.map((n) =>
                      n?.component.kind === "action" ? (
                        runtime.placements.get(n.component.placementId) ? (
                          <ActionView
                            key={n.nodeId}
                            placement={runtime.placements.get(n.component.placementId) as UxPlacement}
                            row={{ bindingId: component.dataBindingId, record: row }}
                          />
                        ) : null
                      ) : n?.component.kind === "navigation" ? (
                        <NavigationView
                          key={n.nodeId}
                          itemId={n.component.itemId}
                          row={{ bindingId: component.dataBindingId, record: row }}
                        />
                      ) : null,
                    )}
                    {runtime.explain ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-xs"
                        aria-label={message(runtime.locale, "list.whyRow", { key: row.key })}
                        onClick={() => runtime.explain?.(row.objectType, row.key)}
                      >
                        {message(runtime.locale, "detail.why")}{" "}
                        <ArrowUpRight className="rtl-mirror size-3" aria-hidden />
                      </Button>
                    ) : null}
                  </div>
                </RecordScope>,
              ]
            : []),
          ...(runtime.host.openGraph
            ? [
                <Button
                  key="graph"
                  size="sm"
                  variant="ghost"
                  onClick={() => runtime.host.openGraph?.(row.objectType, row.key)}
                >
                  {message(runtime.locale, "detail.graph")} <ArrowUpRight className="rtl-mirror" />
                </Button>,
              ]
            : []),
        ],
      }))
      if (ListFrame) return <ListFrame columns={presentedColumns} rows={presentedRows} footer={continuation} />
      return frame(
        <>
          <tbody className="divide-y divide-border-subtle">
            {presentedRows.map((row) => (
              <tr key={row.key} className={cn("align-middle", chosen.includes(row.key) && "bg-primary-soft")}>
                {row.cells.map((cell, index) => (
                  <td key={presentedColumns[index]?.key ?? index} className="px-3 py-1.5">
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
          {continuation ? (
            <tfoot>
              <tr>
                <td colSpan={span} className="border-t border-border-subtle px-3 py-1">
                  {continuation}
                </td>
              </tr>
            </tfoot>
          ) : null}
        </>,
      )
    },
    Detail: ({ props, children }) => {
      const runtime = useRuntime()
      const DetailFrame = useContext(PresentationContext).Detail ?? DefaultDetail
      const node = runtime.nodes.get(props.nodeId)
      if (node?.component.kind !== "detail") return null
      const state = runtime.bindings.get(node.component.dataBindingId)
      if (!state || state.status === "loading") return <Skeleton className="h-32 w-full" />
      if (state.status === "unavailable") return <UnavailableBlock why={state.why} />
      if (state.status === "error") return <ErrorState error={state.error} what={node.component.dataBindingId} inline />
      if (state.status !== "detail") return null
      if (!state.record)
        return (
          <p className="rounded-md border border-border-subtle px-3 py-4 text-sm text-muted-foreground">
            {message(runtime.locale, "detail.notVisible", { objectType: state.objectType })}
          </p>
        )
      return (
        <RecordScope bindingId={node.component.dataBindingId} record={state.record}>
          <DetailFrame
            identity={
              <>
                <code className="font-mono text-sm">{state.record.key}</code>
                <Badge tone="outline">{state.objectType}</Badge>
              </>
            }
            actions={
              <>
                {runtime.host.openGraph ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() =>
                      runtime.host.openGraph?.(state.record?.objectType ?? state.objectType, state.record?.key ?? "")
                    }
                  >
                    {message(runtime.locale, "detail.graph")} <ArrowUpRight className="rtl-mirror" />
                  </Button>
                ) : null}
                {runtime.explain ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="ms-auto text-xs"
                    onClick={() =>
                      runtime.explain?.(state.record?.objectType ?? state.objectType, state.record?.key ?? "")
                    }
                  >
                    {message(runtime.locale, "detail.why")} <ArrowUpRight className="rtl-mirror inline size-3" />
                  </Button>
                ) : null}
              </>
            }
          >
            {children}
          </DetailFrame>
        </RecordScope>
      )
    },
    ChildGrid: ({ props }) => {
      const runtime = useRuntime()
      const formState = useContext(FormContext)
      const node = runtime.nodes.get(props.nodeId)
      if (node?.component.kind !== "child_grid") return null
      const component = node.component
      return (
        <ChildGridView
          key={`${formState?.sessionKey ?? "outside"}:${props.nodeId}`}
          nodeId={props.nodeId}
          formId={component.formId}
          collectionId={component.collectionId}
          label={text(component.label, runtime.locale)}
          orderingKey={component.orderingKey}
          dataBindingId={component.dataBindingId ?? undefined}
          rowOpsInputName={component.rowOpsInputName}
          maxRows={component.maxRows}
          fieldIds={component.fieldIds}
        />
      )
    },
    Form: ({ props, children }) => {
      const runtime = useRuntime()
      const presentation = useContext(PresentationContext)
      const FormFrame = presentation.Form ?? Fragment
      const ActionsFrame = presentation.Actions ?? DefaultActions
      const queryClient = useQueryClient()
      const submit = useSubmit()
      const node = runtime.nodes.get(props.nodeId)
      const form = node?.component.kind === "form" ? runtime.forms.get(node.component.formId) : undefined
      const opened = form ? runtime.openedForms.get(form.formId) : undefined
      // Inline forms use their scoped record or the authoritative detail binding's live record.
      const scopedRecord = useCurrentRecord(form?.dataBindingId ?? "")
      const bindingState = form?.dataBindingId ? runtime.bindings.get(form.dataBindingId) : undefined
      const boundRecord =
        scopedRecord === null
          ? null
          : (scopedRecord ?? (bindingState?.status === "detail" ? bindingState.record : undefined))
      const sourceRecord = opened?.record ?? boundRecord ?? null
      const sourceBindingId = opened?.recordBindingId ?? form?.dataBindingId ?? null
      const hasOpener = form ? [...runtime.placements.values()].some((p) => p.formId === form.formId) : false
      const formId = form?.formId ?? ""
      const [draft, setDraftState] = useState<Record<string, unknown>>(() => prefill(runtime, formId, sourceRecord))
      const [busy, setBusy] = useState(false)
      const [invalid, setInvalidState] = useState<ReadonlySet<string>>(new Set())
      const [result, setResult] = useState<SubmissionResult | null>(null)
      const [submitted, setSubmitted] = useState(false)
      const [gridOperations, setGridOperationsState] = useState<FormState["gridOperations"]>(new Map())
      const [resetEpoch, setResetEpoch] = useState(0)
      const formElement = useRef<HTMLFormElement | null>(null)
      const reloadFocus = useRef<{ sessionKey: string; epoch: number; origin: Element | null } | null>(null)
      const [inlineSession, setInlineSession] = useState(0)
      const [preserveResultForNextSession, setPreserveResultForNextSession] = useState(false)
      const [sessionRecord, setSessionRecord] = useState(sourceRecord)
      const [submissionCount, setSubmissionCount] = useState(0)
      // Reopening for another row starts a fresh draft from that row's snapshot.
      // Data changes cannot retarget an existing edit. Explicit navigation/context changes
      // and form openings establish a new presentation session.
      const inlineRecord = sessionRecord ?? sourceRecord
      const inlineContext = stringifyExact([runtime.scope, runtime.appId, runtime.page.pageId, formId, runtime.context])
      const openKey = opened
        ? `open:${opened.placementId}:${opened.sessionId}:${opened.record?.key ?? ""}:${canonicalVersion(opened.record?.version) ?? "missing-version"}`
        : inlineRecord
          ? `inline:${inlineContext}:${inlineRecord.key}:${inlineSession}`
          : `create:${inlineContext}:${inlineSession}`
      const activeSessionKey = useRef(openKey)
      activeSessionKey.current = openKey
      const [seenKey, setSeenKey] = useState(openKey)
      if (seenKey !== openKey) {
        setSeenKey(openKey)
        setSessionRecord(sourceRecord)
        setDraftState(prefill(runtime, formId, sourceRecord))
        setInvalidState(new Set())
        setGridOperationsState(new Map())
        setBusy(false)
        setSubmitted(false)
        if (!preserveResultForNextSession) setResult(null)
        setPreserveResultForNextSession(false)
      }
      const recordForSession = seenKey === openKey ? sessionRecord : sourceRecord
      const authoritativeRecord =
        recordForSession && sourceBindingId
          ? latestRecordInBinding(runtime.bindings.get(sourceBindingId), recordForSession.key)
          : undefined
      const sessionVersion = canonicalVersion(recordForSession?.version)
      const authoritativeVersion = canonicalVersion(authoritativeRecord?.version)
      const parentUnavailable = Boolean(
        recordForSession && sourceBindingId && (!authoritativeRecord || authoritativeVersion === undefined),
      )
      const editParentUnavailable = form?.mode === "edit" && (!recordForSession || parentUnavailable)
      const parentChanged = Boolean(
        recordForSession &&
          sourceBindingId &&
          !parentUnavailable &&
          (sessionVersion === undefined || authoritativeVersion !== sessionVersion),
      )
      const canReload =
        form?.mode === "create" && !recordForSession
          ? true
          : Boolean(recordForSession && sourceBindingId && authoritativeRecord && authoritativeVersion !== undefined)
      const childGrids = node?.component.kind === "form" ? formChildGrids(node, node.component.formId) : []
      const savedBindingIds = new Set([
        ...(sourceBindingId ? [sourceBindingId] : []),
        ...childGrids.flatMap((grid) =>
          grid.component.kind === "child_grid" && grid.component.dataBindingId ? [grid.component.dataBindingId] : [],
        ),
      ])
      const savedQueries = [...savedBindingIds].flatMap((id) => {
        const key = runtime.bindings.get(id)?.readKey
        const query = key && queryClient.getQueryCache().find({ queryKey: key, exact: true, type: "active" })
        return query ? [query] : []
      })
      const savedSnapshotsReady =
        Boolean(authoritativeRecord && authoritativeVersion !== undefined) &&
        savedQueries.length === savedBindingIds.size &&
        savedQueries.every(
          (query) =>
            query.state.status === "success" && !query.state.isInvalidated && query.state.fetchStatus === "idle",
        ) &&
        savedQueries.every(
          (query) => runtime.bindings.get(query.queryKey[6] as string)?.readHead === query.state.data,
        ) &&
        childGrids.every(
          (grid) =>
            grid.component.kind === "child_grid" &&
            childGridSnapshot(
              grid.component.dataBindingId ? runtime.bindings.get(grid.component.dataBindingId) : undefined,
              grid.component.maxRows,
              grid.component.orderingKey,
              false,
              runtime.host,
              runtime.locale,
            ).kind === "ready",
        )
      useEffect(() => {
        if (
          hasOpener ||
          form?.mode !== "edit" ||
          busy ||
          !submitted ||
          !result?.ok ||
          !savedSnapshotsReady ||
          !authoritativeRecord
        )
          return
        // Acceptance ends this draft. Rebase all collections together, including unchanged
        // grids, only after every relevant read settles with authoritative snapshots.
        setSessionRecord(authoritativeRecord)
        setDraftState(prefill(runtime, formId, authoritativeRecord))
        setInvalidState(new Set())
        setGridOperationsState(new Map())
        setResetEpoch((current) => current + 1)
        setSubmitted(false)
      }, [hasOpener, form?.mode, busy, submitted, result, savedSnapshotsReady, authoritativeRecord, runtime, formId])
      const setGridOperations = useCallback(
        (nodeId: string, inputName: string, operations: readonly unknown[] | null, ready: boolean) => {
          setGridOperationsState((current) => {
            const previous = current.get(nodeId)
            const nextOperations = operations ?? []
            if (
              previous &&
              previous.inputName === inputName &&
              previous.ready === ready &&
              stringifyExact(previous.operations) === stringifyExact(nextOperations)
            )
              return current
            const next = new Map(current)
            next.set(nodeId, { inputName, operations: nextOperations, ready })
            return next
          })
        },
        [],
      )
      const gridsReady =
        seenKey === openKey && childGrids.every((gridNode) => gridOperations.get(gridNode.nodeId)?.ready === true)
      useLayoutEffect(() => {
        const pending = reloadFocus.current
        if (!pending) return
        if (pending.sessionKey !== openKey) {
          reloadFocus.current = null
          return
        }
        if (pending.epoch !== resetEpoch || !gridsReady) return
        reloadFocus.current = null
        // Reload removes its trigger. Respect a user who moved to another control meanwhile.
        if (document.activeElement !== pending.origin && document.activeElement !== document.body) return
        const grid = formElement.current?.querySelector<HTMLElement>("[data-child-grid]")
        const target =
          grid?.querySelector<HTMLElement>('[role="group"]') ??
          grid?.querySelector<HTMLElement>('[data-grid-control="add"]:not(:disabled)') ??
          grid
        target?.focus({ preventScroll: true })
      }, [gridsReady, resetEpoch, openKey])
      if (!form) return null
      // A form with an opener renders only after activation; the activating record prefills it.
      if (hasOpener && !opened) return null
      const setDraft = (name: string, value: unknown) =>
        setDraftState((d) =>
          value === undefined
            ? Object.fromEntries(Object.entries(d).filter(([k]) => k !== name))
            : { ...d, [name]: value },
        )
      const setInvalid = (name: string, bad: boolean) =>
        setInvalidState((current) => {
          const next = new Set(current)
          if (bad) next.add(name)
          else next.delete(name)
          return next
        })
      const reloadSession = () => {
        let latestSessionRecord: RuntimeRecord | null = null
        if (recordForSession && sourceBindingId) {
          const latest = latestRecordInBinding(runtime.bindings.get(sourceBindingId), recordForSession.key)
          if (!latest || canonicalVersion(latest.version) === undefined) return false
          latestSessionRecord = latest
        } else if (form.mode !== "create") {
          return false
        }
        setSessionRecord(latestSessionRecord)
        setDraftState(prefill(runtime, formId, latestSessionRecord))
        setInvalidState(new Set())
        setGridOperationsState(new Map())
        setResetEpoch((current) => current + 1)
        setSubmitted(false)
        setResult(null)
        return true
      }
      const reloadFormWithFocus = () => {
        const origin = document.activeElement
        if (reloadSession()) reloadFocus.current = { sessionKey: openKey, epoch: resetEpoch + 1, origin }
      }
      const retrySavedRefresh = async () => {
        const sessionKey = openKey
        setBusy(true)
        await Promise.allSettled(
          savedQueries.map((query) =>
            queryClient.invalidateQueries({ queryKey: query.queryKey, exact: true }, { throwOnError: true }),
          ),
        )
        if (activeSessionKey.current === sessionKey) setBusy(false)
      }
      const go = async () => {
        if (!gridsReady || editParentUnavailable) return
        setBusy(true)
        // The form submits for the record it was opened for (or its inline binding's record), key included.
        const source =
          recordForSession && sourceBindingId ? { bindingId: sourceBindingId, record: recordForSession } : null
        const submittedDraft = { ...draft }
        const operationsByInput = new Map<string, unknown[]>()
        for (const gridNode of childGrids) {
          const grid = gridOperations.get(gridNode.nodeId)
          if (!grid?.ready) {
            setBusy(false)
            return
          }
          const operations = operationsByInput.get(grid.inputName) ?? []
          operations.push(...grid.operations)
          operationsByInput.set(grid.inputName, operations)
        }
        for (const [inputName, operations] of operationsByInput) submittedDraft[inputName] = operations
        const submittedSessionKey = openKey
        const r = await submit(form.submitBindingId, { draft: submittedDraft, record: source }, () => {
          if (activeSessionKey.current === submittedSessionKey && (hasOpener || form.mode === "edit"))
            setSubmitted(true)
        })
        if (activeSessionKey.current !== submittedSessionKey) return
        setBusy(false)
        if (r.ok) {
          if (hasOpener) setSubmitted(true)
          else if (form.mode === "create") {
            const binding = runtime.actions.get(form.submitBindingId)
            const action =
              binding &&
              runtime.set.artifacts.find(
                (candidate) =>
                  candidate.kind === "action" &&
                  candidate.artifactId === binding.action.id &&
                  candidate.version === binding.action.version,
              )
            const effect = action?.payload.effectProgram
            if (isRecord(effect) && effect.kind === "mutate_parent_with_rows") {
              setPreserveResultForNextSession(true)
              setInlineSession((current) => current + 1)
            }
          }
        }
        setResult(r)
        setSubmissionCount((count) => count + 1)
      }
      const formContents = (
        <FormContext.Provider
          value={{
            formId: form.formId,
            sessionKey: openKey,
            draft,
            setDraft,
            gridOperations,
            setGridOperations,
            invalid,
            setInvalid,
            busy,
            submitted,
            parentChanged,
            parentUnavailable: editParentUnavailable,
            canReload,
            resetEpoch,
            reloadSession,
          }}
        >
          <FormFrame>
            <form
              ref={formElement}
              onSubmit={(e) => e.preventDefault()}
              aria-label={text(form.submit.label, runtime.locale)}
              className={presentation.formClassName ?? "grid gap-3 rounded-lg border border-border bg-card p-4"}
            >
              {opened?.record ? (
                <p className="text-xs text-muted-foreground">
                  {message(runtime.locale, "form.forRecord", { key: opened.record.key })}
                </p>
              ) : null}
              {!hasOpener && form.mode === "edit" && submitted && result?.ok ? (
                <div
                  role="status"
                  className="grid gap-2 rounded-md border border-dashed border-border px-3 py-3 text-sm text-muted-foreground"
                >
                  <span>{message(runtime.locale, "form.savedRefreshPending")}</span>
                  <Button
                    size="sm"
                    type="button"
                    variant="secondary"
                    className="w-fit"
                    disabled={busy}
                    onClick={() => void retrySavedRefresh()}
                  >
                    {message(runtime.locale, "form.retrySavedRefresh")}
                  </Button>
                </div>
              ) : null}
              {form.mode === "edit" && parentChanged && !submitted ? (
                <div
                  role="status"
                  className="grid gap-2 rounded-md border border-dashed border-border px-3 py-3 text-sm text-muted-foreground"
                >
                  <span>{message(runtime.locale, "form.parentChanged")}</span>
                  <Button
                    size="sm"
                    className="w-fit"
                    disabled={!canReload || busy || submitted}
                    onClick={reloadFormWithFocus}
                  >
                    {message(runtime.locale, "form.reloadRows")}
                  </Button>
                </div>
              ) : null}
              {editParentUnavailable && !submitted ? (
                <div
                  role="status"
                  className="rounded-md border border-dashed border-border px-3 py-2 text-sm text-muted-foreground"
                >
                  {message(runtime.locale, "form.parentUnavailable")}
                </div>
              ) : null}
              {invalid.size > 0 ? (
                <div
                  role="status"
                  className="grid gap-2 rounded-md border border-dashed border-border px-3 py-3 text-sm text-muted-foreground"
                >
                  <span>{message(runtime.locale, "form.invalidDraft")}</span>
                  <Button
                    size="sm"
                    type="button"
                    variant="secondary"
                    className="w-fit"
                    disabled={!canReload || busy || submitted}
                    onClick={reloadFormWithFocus}
                  >
                    {message(runtime.locale, "form.reload")}
                  </Button>
                </div>
              ) : null}
              {children}
              <ActionsFrame
                status={
                  result ? (
                    <p
                      role={result.ok ? "status" : "alert"}
                      className={cn("text-xs", result.ok ? "text-success" : "text-destructive")}
                    >
                      {submissionMessage(result, runtime.locale, runtime.host, "form.submitted")}
                    </p>
                  ) : null
                }
              >
                {/* A settled submission closes the confirmation; asking again starts from the button. */}
                <Confirmable
                  key={result ? `settled:${submissionCount}` : "open"}
                  label={text(form.submit.label, runtime.locale)}
                  confirmation={form.submit.confirmation}
                  disabled={busy || submitted || invalid.size > 0 || !gridsReady || editParentUnavailable}
                  onConfirm={() => void go()}
                />
                {hasOpener ? (
                  <Button size="sm" variant="ghost" type="button" onClick={() => runtime.closeForm(form.formId)}>
                    {message(runtime.locale, "common.close")}
                  </Button>
                ) : null}
                {busy ? (
                  <Loader2
                    className="size-4 animate-spin text-muted-foreground"
                    aria-label={message(runtime.locale, "form.submitting")}
                  />
                ) : null}
              </ActionsFrame>
            </form>
          </FormFrame>
        </FormContext.Provider>
      )
      return recordForSession && sourceBindingId ? (
        <RecordScope bindingId={sourceBindingId} record={parentUnavailable ? null : recordForSession}>
          {formContents}
        </RecordScope>
      ) : (
        formContents
      )
    },
    Field: ({ props }) => {
      const runtime = useRuntime()
      const node = runtime.nodes.get(props.nodeId)
      const field = node?.component.kind === "field" ? runtime.fields.get(node.component.fieldId) : undefined
      return field ? (
        <FieldView field={field} />
      ) : (
        <UnavailableBlock compact why={{ reason: message(runtime.locale, "field.undeclared") }} />
      )
    },
    Action: ({ props }) => {
      const runtime = useRuntime()
      const node = runtime.nodes.get(props.nodeId)
      const placement =
        node?.component.kind === "action" ? runtime.placements.get(node.component.placementId) : undefined
      return placement ? <ActionView placement={placement} /> : null
    },
    Navigation: ({ props }) => {
      const runtime = useRuntime()
      const node = runtime.nodes.get(props.nodeId)
      return node?.component.kind === "navigation" ? <NavigationView itemId={node.component.itemId} /> : null
    },
    Unsupported: ({ props }) => <UnavailableBlock why={{ reason: props.reason }} />,
  },
})

function entityForRef(
  runtime: PageRuntime,
  ref: { readonly id: string; readonly version: string },
): WidgetArtifact | undefined {
  return runtime.set.artifacts.find(
    (artifact) => artifact.kind === "entity" && artifact.artifactId === ref.id && artifact.version === ref.version,
  )
}

function entityForForm(runtime: PageRuntime, formId: string): WidgetArtifact | undefined {
  const form = runtime.forms.get(formId)
  if (!form) return undefined
  if (form.dataBindingId) {
    const binding = runtime.ux.dataBindings.find((candidate) => candidate.bindingId === form.dataBindingId)
    if (binding) return entityForRef(runtime, binding.object)
  }
  const binding = runtime.actions.get(form.submitBindingId)
  if (!binding) return undefined
  const action = runtime.set.artifacts.find(
    (candidate) =>
      candidate.kind === "action" &&
      candidate.artifactId === binding.action.id &&
      candidate.version === binding.action.version,
  )
  const effect = action?.payload.effectProgram
  if (!isRecord(effect)) return undefined
  const artifactId = asString(effect.entityArtifactId)
  const version = asString(effect.entityArtifactVersion)
  return artifactId && version ? entityForRef(runtime, { id: artifactId, version }) : undefined
}

function objectTypeName(artifact: WidgetArtifact | undefined): string | undefined {
  if (!artifact) return undefined
  return objectTypeOf({ artifacts: [artifact] }, { id: artifact.artifactId, version: artifact.version }) ?? undefined
}

/** Presentation of an admitted effective contract; Rust remains the admission authority. */
function entityLineage(runtime: PageRuntime, artifact: WidgetArtifact | undefined): readonly Record<string, unknown>[] {
  const lineage: Record<string, unknown>[] = []
  const seen = new Set<string>()
  let current = artifact?.payload.objectType
  while (isRecord(current)) {
    const name = asString(current.name)
    if (!name || seen.has(name)) return []
    seen.add(name)
    lineage.push(current)
    if (current.extends === undefined) return lineage
    const parentName = asString(current.extends)
    const parent = runtime.set.artifacts.find(
      (candidate) => candidate.kind === "entity" && objectTypeName(candidate) === parentName,
    )
    current = parent?.payload.objectType
  }
  return []
}

function effectiveProperty(
  runtime: PageRuntime,
  artifact: WidgetArtifact | undefined,
  propertyId: string,
): { type: Record<string, unknown>; nullable: boolean; defaultValue: unknown; computed: boolean } | undefined {
  const declarations = entityLineage(runtime, artifact).flatMap((type) => {
    const extensions = runtime.set.artifacts
      .filter((candidate) => candidate.kind === "entity")
      .map((candidate) => candidate.payload.objectTypeExtension)
      .filter((extension) => isRecord(extension) && extension.target === type.name)
      .flatMap((extension) => (isRecord(extension) && Array.isArray(extension.properties) ? extension.properties : []))
    return [...(Array.isArray(type.properties) ? type.properties : []), ...extensions].filter(
      (property): property is Record<string, unknown> => isRecord(property) && property.id === propertyId,
    )
  })
  const property = declarations[0]
  if (!property) return undefined
  let type = property.type
  let nullable = isRecord(property.constraints) && property.constraints.nullable === true
  const seen = new Set<string>()
  while (isRecord(type) && type.kind === "ref") {
    const name = asString(type.name)
    if (!name || seen.has(name)) return undefined
    seen.add(name)
    const definition = runtime.set.artifacts
      .filter((candidate) => candidate.kind === "entity")
      .flatMap((candidate) => (Array.isArray(candidate.payload.propertyTypes) ? candidate.payload.propertyTypes : []))
      .find((candidate) => isRecord(candidate) && candidate.name === name)
    if (!isRecord(definition)) return undefined
    nullable ||= isRecord(definition.constraints) && definition.constraints.nullable === true
    type = definition.type
  }
  if (!isRecord(type)) return undefined
  return {
    type,
    nullable,
    defaultValue: declarations.find((declaration) => Object.hasOwn(declaration, "default"))?.default,
    computed: property.computed !== undefined,
  }
}

/** Generated parent identities are immutable even in a permissive presentation fixture. */
function generatedProperty(runtime: PageRuntime, artifact: WidgetArtifact | undefined, propertyId: string): boolean {
  return entityLineage(runtime, artifact).some(
    (type) =>
      Array.isArray(type.identity) &&
      type.identity.some(
        (key) =>
          isRecord(key) &&
          isRecord(key.namingSeries) &&
          Array.isArray(key.properties) &&
          key.properties.includes(propertyId),
      ),
  )
}

function initialBooleanValue(
  runtime: PageRuntime,
  artifact: WidgetArtifact | undefined,
  propertyId: string,
): boolean | undefined {
  const property = effectiveProperty(runtime, artifact, propertyId)
  if (property) {
    if (property.computed || property.type.kind !== "boolean") return undefined
    if (typeof property.defaultValue === "boolean") return property.defaultValue
    return property.nullable ? undefined : false
  }
  const fields = artifact?.payload.fields
  const legacy = isRecord(fields) ? fields[propertyId] : undefined
  return isRecord(legacy) && legacy.type === "boolean" && legacy.required === true ? false : undefined
}

function schemaCheckboxValue(
  runtime: PageRuntime,
  field: UxField,
  artifact: WidgetArtifact | undefined,
  propertyId: string,
): boolean | undefined {
  if (field.control !== "checkbox") return undefined
  return initialBooleanValue(runtime, artifact, propertyId)
}

/** Neutral containers keep the form surface; nested forms own their own grids. */
function formChildGrids(node: UxNode, formId: string): UxNode[] {
  return node.children.flatMap((child) => {
    if (child.component.kind === "form") return []
    if (child.component.kind === "child_grid") return child.component.formId === formId ? [child] : []
    return formChildGrids(child, formId)
  })
}

function childEntityForGrid(
  runtime: PageRuntime,
  formId: string,
  collectionId: string,
  dataBindingId?: string,
): WidgetArtifact | undefined {
  if (dataBindingId) {
    const binding = runtime.ux.dataBindings.find((candidate) => candidate.bindingId === dataBindingId)
    if (binding) return entityForRef(runtime, binding.object)
  }
  const parent = entityForForm(runtime, formId)
  const collection = entityLineage(runtime, parent)
    .flatMap((type) => (Array.isArray(type.ownedCollections) ? type.ownedCollections : []))
    .find((candidate) => isRecord(candidate) && candidate.id === collectionId)
  const childType = isRecord(collection) ? asString(collection.childType) : undefined
  if (!childType) return undefined
  return runtime.set.artifacts.find((artifact) => artifact.kind === "entity" && objectTypeName(artifact) === childType)
}

/** Derivations belong to the submit action, so their targets never become caller values. */
function derivedChildProperties(runtime: PageRuntime, formId: string, collectionId: string): ReadonlySet<string> {
  const form = runtime.forms.get(formId)
  const binding = form && runtime.actions.get(form.submitBindingId)
  const action =
    binding &&
    runtime.set.artifacts.find(
      (candidate) =>
        candidate.kind === "action" &&
        candidate.artifactId === binding.action.id &&
        candidate.version === binding.action.version,
    )
  const effect = action?.payload.effectProgram
  if (!isRecord(effect) || effect.kind !== "mutate_parent_with_rows" || !Array.isArray(effect.collections))
    return new Set()
  const collection = effect.collections.find((candidate) => isRecord(candidate) && candidate.id === collectionId)
  return new Set(isRecord(collection) && isRecord(collection.derive) ? Object.keys(collection.derive) : [])
}

/** Closed controls retain the declared value type; DOM option indices are only presentation keys. */
function fieldChoices(
  runtime: PageRuntime,
  field: UxField,
  rowBindingId?: string,
): readonly { value: unknown; label: string }[] | null {
  if (field.optionQueryBindingId) {
    const binding = runtime.ux.dataBindings.find((candidate) => candidate.bindingId === field.optionQueryBindingId)
    const state = runtime.bindings.get(field.optionQueryBindingId)
    const entity = binding && entityForRef(runtime, binding.object)
    const identities = entityLineage(runtime, entity).flatMap((type) =>
      Array.isArray(type.identity) ? type.identity : [],
    )
    if (state?.status !== "list" || state.duplicateKeys?.size || identities.length !== 1) return null
    const identity = identities[0]
    const key =
      isRecord(identity) && Array.isArray(identity.properties) && identity.properties.length === 1
        ? asString(identity.properties[0])
        : undefined
    if (!key || state.rows.some((record) => !isRecord(record.values) || !Object.hasOwn(record.values, key))) return null
    return state.rows.map((record) => ({ value: (record.values as Record<string, unknown>)[key], label: record.key }))
  }
  const target = field.target
  if (target.kind === "form_input") {
    const form = runtime.forms.get(target.formId)
    const binding = form && runtime.actions.get(form.submitBindingId)
    const action =
      binding &&
      runtime.set.artifacts.find(
        (artifact) =>
          artifact.kind === "action" &&
          artifact.artifactId === binding.action.id &&
          artifact.version === binding.action.version,
      )
    const schema = action?.payload.inputSchema
    const property = isRecord(schema) && isRecord(schema.properties) ? schema.properties[target.inputName] : undefined
    return isRecord(property) && Array.isArray(property.enum)
      ? property.enum.map((value) => ({ value, label: editText(value) }))
      : null
  }
  const entity =
    target.kind === "child_row_property"
      ? childEntityForGrid(runtime, target.formId, target.collectionId, rowBindingId)
      : target.kind === "parent_property"
        ? entityForForm(runtime, target.formId)
        : undefined
  const propertyId =
    target.kind === "child_row_property" || target.kind === "parent_property" ? target.propertyId : undefined
  const valueType = propertyId ? effectiveProperty(runtime, entity, propertyId)?.type : undefined
  if (!isRecord(valueType) || valueType.kind !== "enum" || !Array.isArray(valueType.values)) return null
  return valueType.values.flatMap((entry) => {
    if (!isRecord(entry) || typeof entry.value !== "string") return []
    const label = isRecord(entry.label)
      ? (asString(entry.label[runtime.locale]) ?? asString(entry.label.en))
      : undefined
    return [{ value: entry.value, label: label ?? entry.value }]
  })
}

function initialChildCheckboxes(
  runtime: PageRuntime,
  formId: string,
  collectionId: string,
  dataBindingId: string | undefined,
  fields: readonly UxField[],
): Record<string, boolean> {
  const entity = childEntityForGrid(runtime, formId, collectionId, dataBindingId)
  const values: Record<string, boolean> = {}
  for (const field of fields) {
    if (
      field.target.kind !== "child_row_property" ||
      field.target.formId !== formId ||
      field.target.collectionId !== collectionId
    )
      continue
    const value = schemaCheckboxValue(runtime, field, entity, field.target.propertyId)
    if (value !== undefined) values[field.target.propertyId] = value
  }
  return values
}

/** Form inputs prefilled from the activating record's `initial` properties. */
function prefill(runtime: PageRuntime, formId: string, record: { values: unknown } | null): Record<string, unknown> {
  const fields = runtime.fields
  const form = runtime.forms.get(formId)
  const parentEntity = entityForForm(runtime, formId)
  const draft: Record<string, unknown> = {}
  for (const f of fields.values()) {
    if (f.target.kind === "parent_property" && f.target.formId === formId) {
      const current = isRecord(draft[f.target.inputName]) ? (draft[f.target.inputName] as Record<string, unknown>) : {}
      if (!Object.hasOwn(draft, f.target.inputName)) draft[f.target.inputName] = current
      // Display-only parent fields render from the authoritative record but must
      // never be copied into the mutation patch. An empty object is still seeded
      // for row-only forms, while lifecycle/computed values stay read-only.
      if (f.control === "display" || generatedProperty(runtime, parentEntity, f.target.propertyId)) continue
      if (record && isRecord(record.values) && Object.hasOwn(record.values, f.target.propertyId)) {
        draft[f.target.inputName] = { ...current, [f.target.propertyId]: record.values[f.target.propertyId] }
      } else if (!record && form?.mode === "create") {
        const value = schemaCheckboxValue(runtime, f, parentEntity, f.target.propertyId)
        if (value !== undefined) draft[f.target.inputName] = { ...current, [f.target.propertyId]: value }
      }
    }
    if (record && f.target.kind === "form_input" && f.target.formId === formId && f.target.initial) {
      const v = (record.values as Record<string, unknown>)[f.target.initial.propertyId]
      if (v !== undefined) draft[f.target.inputName] = v
    }
  }
  return draft
}

/** Resolve a pinned parent key from the latest admitted detail or list snapshot. */
function latestRecordInBinding(state: BindingState | undefined, key: string): RuntimeRecord | undefined {
  if (state?.status === "detail") return state.record?.key === key ? state.record : undefined
  if (state?.status === "list" && !state.duplicateKeys?.has(key)) return state.rows.find((record) => record.key === key)
  return undefined
}
